import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import "./MyAccountFilters.css";
import filterIcon from "../../../../icons/Filter.svg";
import * as translator from "../../../../utils/translator";
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import Select from "../../../../components/Select/Select";

interface MyAccountFiltersProps {
  totalOrders: number;
  orderArray: MyAccountOrder[];
  ordersStatuses: string[];
  country: string;
  hasCarePlus: boolean;
  ordersProducts: {
    id: number;
    title: string;
    localeID?: string;
  }[];
}

const MyAccountFilters: React.FC<MyAccountFiltersProps> = ({
  totalOrders,
  orderArray,
  ordersStatuses,
  ordersProducts,
  country,
  hasCarePlus,
}) => {
  const { language } = useOutletContext<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const statuses = ordersStatuses.map((status: string) => ({
    id: status,
    value: translator.translate(language, "my_account", `MyOrders/status/${status}`) ?? status
  }));
  
  const allSatuses = translator.translate(
    language,
    "my_account",
    "MyOrders/filters/allOrders"
  );
  const statusesWithAll = [{id: -1, value: allSatuses}, ...statuses];

  const allProducts = translator.translate(
    language,
    "my_account",
    "MyOrders/filters/allProducts"
  );

  const CARE_PLUS_FILTER_ID = -2;
  const carePlusFilterOption = { id: CARE_PLUS_FILTER_ID, value: translator.translate(language, "my_account", "MyOrders/filters/carePlusTreatmentPlan") };
  const ordersCountryProducts = ordersProducts.filter((product) => !product.localeID || product.localeID.split('-')[1] === country.toUpperCase());
  const productOptions = [
    ...ordersCountryProducts.map((product) => ({ id: product.id, value: product.title })),
    ...(hasCarePlus ? [carePlusFilterOption] : []),
  ];
  const productsTitlesWithAll = [{ id: -1, value: allProducts }, ...productOptions];
  
  const times = [
    {id: -1, value: translator.translate(language, "my_account", "MyOrders/filters/allTime")},
    {id: 30, value: translator.translate(language, "my_account", "MyOrders/filters/last30Days")},
    {id: 60, value: translator.translate(language, "my_account", "MyOrders/filters/last60Days")},
    {id: 90, value: translator.translate(language, "my_account", "MyOrders/filters/last90Days")},
    {id: 365, value: translator.translate(language, "my_account", "MyOrders/filters/lastYear")},
  ];

  const [filters, setFilters] = useState<{
    productID: number;
    status: number | string;
    time: number;
  }>({
    productID: searchParams.get('carePlus') === '1' ? CARE_PLUS_FILTER_ID : (searchParams.get('productID') ? parseInt(searchParams.get('productID')!) : -1),
    status: searchParams.get('status') ? searchParams.get('status')! : -1,
    time: searchParams.get('time') ? parseInt(searchParams.get('time')!) : times[0].id,
  });

  useEffect(() => {

    const urlSearchParams = new URLSearchParams(searchParams);

    if (searchParams.get("offset")) {
      urlSearchParams.set('offset', searchParams.get("offset")!);
    }

    if (searchParams.get("limit")) {
      urlSearchParams.set('limit', searchParams.get("limit")!);
    }

    if (filters.productID === CARE_PLUS_FILTER_ID) {
      urlSearchParams.set('carePlus', '1');
      urlSearchParams.delete('productID');
      urlSearchParams.delete('offset');
      urlSearchParams.delete('limit');
    } else if (filters.productID !== -1) {
      urlSearchParams.set('productID', filters.productID.toString());
      urlSearchParams.delete('carePlus');
      urlSearchParams.delete('offset');
      urlSearchParams.delete('limit');
    } else {
      urlSearchParams.delete('productID');
      urlSearchParams.delete('carePlus');
    }

    if (filters.status !== -1) {
      urlSearchParams.set('status', filters.status.toString());
      urlSearchParams.delete('offset');
      urlSearchParams.delete('limit');
    } else if (filters.status === -1) {
      urlSearchParams.delete('status');
    }

    if (filters.time !== -1) {
      urlSearchParams.set('time', filters.time.toString());
      urlSearchParams.delete('offset');
      urlSearchParams.delete('limit');
    } else if (filters.time === -1) {
      urlSearchParams.delete('time');
    }

    

    setSearchParams(urlSearchParams);
  }, [filters]);
  
  const [mobileFilters, setMobileFilters] = useState<{productID?: number; status?: number | string; time?: number}>({
    productID: searchParams.get('carePlus') === '1' ? CARE_PLUS_FILTER_ID : (searchParams.get('productID') ? parseInt(searchParams.get('productID')!) : -1),
    status: searchParams.get('status') ? searchParams.get('status')! : -1,
    time: searchParams.get('time') ? parseInt(searchParams.get('time')!) : times[0].id,
  });

  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <div className="my-orders-filters">
      {/* Desktop view for statuses */}
      <div className="my-orders-status-tabs my-orders-desktop-only">
        {statusesWithAll.map((status, idx) => (
          <div
            key={`${status}-${idx}`}
            className={`status-tab ${
              filters.status === status.id ? "active" : ""
            }`}
            onClick={() => setFilters({ ...filters, status: status.id })}
          >
            {status.value}
          </div>
        ))}
      </div>

      {/* Mobile view - Status dropdown and filter button */}
      <div className="my-orders-filters-mobile mobile-only">
        <div className="my-orders-mobile-status-dropdown">
          <Select
            initialValue={statusesWithAll.find((s) => s.id === filters.status)}
            id="my-orders-dropdown-status"
            placeholder={translator.translate(
              language,
              "my_account",
              "MyOrders/filters/chooseStatus"
            )}
            errorMode={false}
            options={statusesWithAll.map((s) => {
              return { value: s.value, id: s.id };
            }) as never[]}
            onChangeCallback={({ option }) =>
              setFilters({ ...filters, status: option?.id })
            }
            style={{
              border: "1px solid #aaa",
              height: "44px",
              width: "100%",
              borderRadius: "4px",
              backgroundColor: "var(--primary-white, white)",
              margin: "0px",
            }}
          />
        </div>
        <button className="my-orders-filter-button" onClick={toggleModal}>
          <img src={filterIcon} alt="Filter" />
        </button>
      </div>

      {/* Desktop view - Filters row */}
      <div className="my-orders-filters-row my-orders-desktop-only">
        <div className="my-orders-order-count">
          <span className="my-orders-order-count">
            {totalOrders +
              " " +
              translator.translate(
                language,
                "my_account",
                "MyOrders/filters/orders"
              )}
          </span>
          {" " +
            translator.translate(
              language,
              "my_account",
              "MyOrders/filters/placedIn"
            )}
        </div>

        {/* Time frame dropdown */}
        <div className="my-orders-filter-dropdown">
          <Select
            initialValue={times.find((t) => t.id === filters.time)}
            id="my-orders-dropdown-time"
            placeholder={translator.translate(
              language,
              "my_account",
              "MyOrders/filters/chooseDate"
            )}
            errorMode={false}
            options={times.map((t) => {
              return { value: t.value, id: t.id };
            }) as never[]}
            onChangeCallback={({ option }) =>
              setFilters({ ...filters, time: option.id })
            }
            style={{
              border: "1px solid #aaa",
              height: "44px",
              width: "183px",
              borderRadius: "4px",
              backgroundColor: "var(--primary-white, white)",
              margin: "0px",
            }}
            directionUp={orderArray.length === 0}
          />
        </div>

        {/* Products dropdown */}
        <div className="my-orders-filter-dropdown">
          <Select
            initialValue={productsTitlesWithAll.find((p) => p.id === filters.productID)}
            id="my-orders-dropdown-product"
            placeholder={translator.translate(
              language,
              "my_account",
              "MyOrders/filters/selectProduct"
            )}
            errorMode={false}
            options={productOptions.map((pt) => {
              return { value: pt.value, id: pt.id };
            }) as never[]}
            onChangeCallback={({ option, index }) =>
              setFilters({ ...filters, productID: option.id })
            }
            style={{
              border: "1px solid #aaa",
              height: "44px",
              width: "415px",
              borderRadius: "4px",
              backgroundColor: "var(--primary-white, white)",
              margin: "0px",
            }}
            directionUp={orderArray.length == 0}
          />
        </div>
      </div>

      {/* Mobile modal */}
      <div className={`my-orders-filters-modal ${modalOpen ? "open" : ""}`}>
        <div className="my-orders-modal-header">
          <h3>
            {translator.translate(
              language,
              "my_account",
              "MyOrders/filters/sorting"
            )}
          </h3>
          <button className="my-orders-close-modal" onClick={closeModal}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L13 13M1 13L13 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="my-orders-modal-section">
          <h4>
            {translator.translate(
              language,
              "my_account",
              "MyOrders/filters/sortByDate"
            )}
          </h4>
          <div className="my-orders-radio-group">
            {times.map((time) => (
              <label key={time.value} className="my-orders-radio-label">
                <input
                  type="radio"
                  name="time"
                  value={time.value}
                  checked={mobileFilters.time !== undefined ? mobileFilters.time === time.id : filters.time === time.id}
                  onChange={() => (setMobileFilters({ ...mobileFilters, time: time.id }))}
                />
                <span className="my-orders-radio-text">{time.value}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="my-orders-modal-section">
          <h4>
            {translator.translate(
              language,
              "my_account",
              "MyOrders/filters/sortByProduct"
            )}
          </h4>
          <div className="my-orders-radio-group">
            {productsTitlesWithAll.map((title) => (
              <label key={title.id} className="my-orders-radio-label">
                <input
                  type="radio"
                  name="productTitle"
                  value={title.id}
                  checked={mobileFilters.productID !== undefined ? mobileFilters.productID === title.id : filters.productID === title.id}
                  onChange={() =>
                    (setMobileFilters({ ...mobileFilters, productID: title.id }))
                  }
                />
                <span className="my-orders-radio-text">{title.value}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="my-orders-modal-footer">
          <SubmitButton
            id="my-orders-apply-filter"
            placeholder={translator.translate(
              language,
              "my_account",
              "MyOrders/filters/apply"
            )}
            onSubmit={(e: SubmitEvent) => {
              e.preventDefault();
              setFilters({ ...filters, ...mobileFilters });
              closeModal();
            }}
          />
        </div>
      </div>

      {/* Overlay for when modal is open */}
      {modalOpen && (
        <div className="my-orders-modal-overlay" onClick={closeModal}></div>
      )}
    </div>
  );
};

export default MyAccountFilters;
